import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DemoPersistBlockedError } from '../lib/demoPersist';
import { useDemoPersistStore } from '../store/demoPersist.store';
import { useAuthStore } from '../store/auth.store';
import { useMerchantDemo } from './useMerchantDemo';

/** Bloque les écritures en démo (commerçant, livreur, etc.) et affiche un toast explicite. */
export function useDemoPersistGuard() {
  const user = useAuthStore((s) => s.user);
  const { isDemoMerchant } = useMerchantDemo();
  const isDemoUser = user?.isDemo === true;
  const isDemoDriver = user?.role === 'driver' && isDemoUser;
  const canPersist = !isDemoUser;
  const { t } = useTranslation();
  const showBlocked = useDemoPersistStore((s) => s.showBlocked);

  const blockedMessage = isDemoDriver
    ? t('driverDemo.persistBlocked')
    : t('merchantDemo.persistBlocked');

  const notifyBlocked = useCallback(() => {
    if (!canPersist) {
      showBlocked(blockedMessage);
      return true;
    }
    return false;
  }, [canPersist, showBlocked, blockedMessage]);

  const guardPersist = useCallback(() => {
    if (notifyBlocked()) {
      throw new DemoPersistBlockedError();
    }
  }, [notifyBlocked]);

  const runPersist = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    guardPersist();
    return fn();
  }, [guardPersist]);

  return { isDemoMerchant, isDemoDriver, isDemoUser, canPersist, notifyBlocked, guardPersist, runPersist };
}
